import React, { FunctionComponent, useEffect, useState } from 'react';
import { ReactQueryDevtools } from 'react-query/devtools';
import { GlobalProviders } from '@/contexts';
import { MetaTagsSEO, Router } from '..';
import { GlobalStyles } from '.';
import { ErrorBoundary } from './GlobalErrorBoundary';

// Lazy load analytics to improve initial load time
const loadAnalytics = () => {
  if (import.meta.env.VITE_NODE_ENV !== 'production') {
    return;
  }

  // Load Google Tag Manager
  import('react-gtm-module').then((TagManager) => {
    const tagManagerArgs = {
      gtmId: 'GTM-5W6HFVH'
    };
    TagManager.default.initialize(tagManagerArgs);
  });

  // Load Google Analytics
  import('react-ga').then((ReactGA) => {
    ReactGA.default.initialize('UA-177234032-1');
  });

  // Load Facebook Pixel
  if (!window._fbqInitialized && typeof window.fbq === 'undefined') {
    try {
      (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
        if (f.fbq) return;
        n = f.fbq = function (...args: any[]) {
          n.callMethod ? n.callMethod(...args) : n.queue.push(args);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

      setTimeout(() => {
        if (typeof window.fbq === 'function' && !window._fbqInitialized) {
          window.fbq('init', '750381646381609');
          window._fbqInitialized = true;
        }
      }, 2000); // Increase delay for better initial load
    } catch (error) {
      console.error('❌ Error initializing Facebook Pixel:', error);
    }
  }
};

const appHeight = () => {
  const doc = document.documentElement;
  doc.style.setProperty('—app-height', `${window.innerHeight}px`);
};

export const App: FunctionComponent = () => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Set app height
    appHeight();
    window.addEventListener('resize', appHeight);

    // Defer analytics loading
    const timer = setTimeout(() => {
      loadAnalytics();
    }, 1000); // Load analytics after 1 second

    setIsInitialized(true);

    return () => {
      window.removeEventListener('resize', appHeight);
      clearTimeout(timer);
    };
  }, []);

  // Add chunk load error handling
  useEffect(() => {
    const handleChunkLoadError = (event: any) => {
      if (
        event.message?.includes('Failed to fetch dynamically imported module') ||
        event.message?.includes('Importing a module script failed') ||
        event.message?.includes('ChunkLoadError') ||
        event.message?.includes('Loading chunk')
      ) {
        console.error('Detected chunk load error, refreshing page:', event);
        window.location.reload();
      }
    };

    window.addEventListener('error', handleChunkLoadError);
    window.addEventListener('unhandledrejection', (event) => handleChunkLoadError(event.reason));
    window.addEventListener('vite:preloadError', (event) => {
      console.error('Vite preload error, refreshing page:', event);
      window.location.reload();
    });

    return () => {
      window.removeEventListener('error', handleChunkLoadError);
      window.removeEventListener('unhandledrejection', (event) => handleChunkLoadError(event.reason));
      window.removeEventListener('vite:preloadError', () => {
        window.location.reload();
      });
    };
  }, []);

  if (!isInitialized) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#f8f9fa'
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <GlobalProviders>
      <MetaTagsSEO title="Ágora | Red de profesionales únicos" />
      <ErrorBoundary>
        <Router />
        <GlobalStyles />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </ErrorBoundary>
    </GlobalProviders>
  );
};
